import React from 'react';
import { COLUMN_TEMPLATE } from './productGridConfig';
import ActionMenu from '../common/ActionMenu';

const cellBase = {
  padding: '0.2rem 0.15rem',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  display: 'flex',
  alignItems: 'center',
  fontSize: '10px',
  minWidth: 0
};

const editInput = (extra = {}) => ({
  width: '100%',
  padding: '0.15rem 0.3rem',
  border: '1px solid var(--info)',
  borderRadius: '3px',
  fontSize: '9px',
  boxSizing: 'border-box',
  ...extra
});

const actionBtn = (bg) => ({
  padding: '4px 10px',
  backgroundColor: bg,

  color: '#fff' ,
  cursor: 'pointer',
  fontSize: '11px',
  lineHeight: 1
});

const VirtualizedProductRow = ({
  index, style,
  items, selectedProducts, editingProduct, tempValues,
  onSelectProduct, onEditProduct, onSaveProduct, onCancelEdit,
  onDeleteProduct, onShowHistory, onToggleDiscountExclusion,
  updateTempValue, commitTempValue,
  canEditProducts, canDeleteProducts, canChangePrices, canExcludeFromDiscounts,
  categories, vatRates
}) => {

  const product = items[index];
  if (!product) return null;

  const isEditing = editingProduct && editingProduct.id === product.id;
  const isSelected = selectedProducts.includes(product.id);
  const bgColor = isEditing ? 'var(--warning-light)' : index % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)';

  return (
    <div style={{
      ...style,
      display: 'grid',
      gridTemplateColumns: COLUMN_TEMPLATE,
      borderBottom: '1px solid var(--border)',
      backgroundColor: bgColor,
      boxSizing: 'border-box'
    }}>
      {/* Checkbox */}
      <div style={{ ...cellBase, justifyContent: 'center' }}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onSelectProduct(product.id)}
          style={{ width: '13px', height: '13px', margin: 0 }}
        />
      </div>

      {/* Lp */}
      <div style={{ ...cellBase, justifyContent: 'center', color: 'var(--text-muted)', fontSize: '9px' }}>
        {index + 1}
      </div>

      {/* ID */}
      <div style={{ ...cellBase, justifyContent: 'center', color: 'var(--text-muted)', fontSize: '9px' }}>
        {product.id}
      </div>

      {/* Nazwa */}
      <div style={{ ...cellBase, padding: '0.2rem 0.3rem', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center' }}>
        {isEditing ? (
          <input
            style={editInput()}
            value={tempValues.nazwa !== undefined ? tempValues.nazwa : editingProduct.nazwa}
            onChange={(e) => updateTempValue('nazwa', e.target.value)}
            onBlur={() => commitTempValue('nazwa')}
            onKeyDown={(e) => e.key === 'Enter' && commitTempValue('nazwa')}
          />
        ) : (
          <>
            <div style={{ fontWeight: '500', fontSize: '10px', lineHeight: '1.2', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
              {product.nazwa || product.name || '-'}
            </div>
            {(product.opis || product.description) && (
              <div style={{ fontSize: '9px', color: 'var(--text-muted)', lineHeight: '1.1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                {(product.opis || product.description).substring(0, 40)}...
              </div>
            )}
          </>
        )}
      </div>

      {/* Kod/EAN */}
      <div style={{ ...cellBase, justifyContent: 'center', flexDirection: 'column' }}>
        {isEditing ? (
          <div style={{ width: '100%' }}>
            <input style={editInput({ marginBottom: '2px' })} placeholder="Kod"
              value={tempValues.kod_produktu !== undefined ? tempValues.kod_produktu : editingProduct.kod_produktu}
              onChange={(e) => updateTempValue('kod_produktu', e.target.value)}
              onBlur={() => commitTempValue('kod_produktu')}
            />
            <input style={editInput()} placeholder="EAN"
              value={tempValues.ean !== undefined ? tempValues.ean : (editingProduct.ean || '')}
              onChange={(e) => updateTempValue('ean', e.target.value)}
              onBlur={() => commitTempValue('ean')}
            />
          </div>
        ) : (
          <>
            <div style={{ fontSize: '9px', fontFamily: 'monospace', backgroundColor: 'var(--bg)', padding: '0.1rem 0.2rem', borderRadius: '2px' }}>
              {product.kod_produktu || product.barcode || '-'}
            </div>
            <div style={{ fontSize: '8px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '2px' }}>
              {product.ean || ''}
              {product.ean_count > 1 && (
                <span style={{ fontSize: '7px', border: '1px solid var(--info)', color: 'var(--info)' , padding: '1px 3px', borderRadius: '2px', fontWeight: 'bold' }}>
                  +{product.ean_count - 1}
                </span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Kategoria */}
      <div style={{ ...cellBase, justifyContent: 'center' }}>
        {isEditing ? (
          <select style={editInput({ padding: '0.1rem' })}
            value={tempValues.kategoria !== undefined ? tempValues.kategoria : editingProduct.category_id || ''}
            onChange={(e) => updateTempValue('kategoria', e.target.value)}
            onBlur={() => commitTempValue('kategoria')}
          >
            <option value="">--</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.display_name}</option>
            ))}
          </select>
        ) : (
          <span style={{ padding: '1px 4px', backgroundColor: 'var(--border)', borderRadius: '2px', fontSize: '9px', maxWidth: '75px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {product.category_name || product.kategoria || product.category || '-'}
          </span>
        )}
      </div>

      {/* Cena sprzedaży */}
      <div style={{ ...cellBase, justifyContent: 'flex-end', padding: '0.2rem' }}>
        {isEditing ? (
          <div>
            <input type="number" step="0.01" disabled={!canChangePrices}
              style={editInput({ width: '60px', backgroundColor: !canChangePrices ? 'var(--border)' : 'var(--card-bg)' })}
              value={tempValues.cena_specjalna !== undefined ? tempValues.cena_specjalna : editingProduct.cena_specjalna}
              onChange={(e) => canChangePrices && updateTempValue('cena_specjalna', e.target.value)}
              onBlur={() => canChangePrices && commitTempValue('cena_specjalna')}
            />
            {editingProduct.suggested_price && (
              <small style={{ color: 'var(--text-muted)', fontSize: '8px', display: 'block' }}>
                Sug: {parseFloat(editingProduct.suggested_price).toFixed(2)}
              </small>
            )}
          </div>
        ) : (
          <div style={{ fontWeight: '600', fontSize: '10px', textAlign: 'right' }}>
            <div style={{ color: product.has_special_price ? 'var(--danger)' : 'var(--success)' }}>
              {parseFloat(product.price || product.cena_sprzedazy_brutto || 0).toFixed(2)}
              {product.has_special_price && (
                <span style={{ fontSize: '7px', border: '1px solid var(--danger)', color: 'var(--danger)' , padding: '1px 2px', borderRadius: '2px', marginLeft: '2px', verticalAlign: 'super' }}>S</span>
              )}
            </div>
            {product.has_special_price && product.suggested_price_brutto && (
              <small style={{ color: 'var(--text-muted)', fontSize: '8px', textDecoration: 'line-through' }}>
                {parseFloat(product.suggested_price_brutto).toFixed(2)}
              </small>
            )}
          </div>
        )}
      </div>

      {/* Cena zakupu */}
      <div style={{ ...cellBase, justifyContent: 'flex-end', padding: '0.2rem' }}>
        {isEditing ? (
          <input type="number" step="0.01" disabled={!canChangePrices}
            style={editInput({ width: '55px', backgroundColor: !canChangePrices ? 'var(--border)' : 'var(--card-bg)' })}
            value={tempValues.cena_zakupu_netto !== undefined ? tempValues.cena_zakupu_netto : (editingProduct.cena_zakupu_netto || editingProduct.purchase_price || '')}
            onChange={(e) => canChangePrices && updateTempValue('cena_zakupu_netto', e.target.value)}
            onBlur={() => canChangePrices && commitTempValue('cena_zakupu_netto')}
            placeholder="netto"
          />
        ) : (
          <span style={{ color: 'var(--text-muted)', fontSize: '9px', lineHeight: '1.3' }}>
            <span style={{ display: 'block' }}>{product.cena_zakupu_netto ? parseFloat(product.cena_zakupu_netto).toFixed(2) : '-'} <span style={{ fontSize: '7px' }}>netto</span></span>
            <span style={{ display: 'block', color: 'var(--text-secondary)' }}>{product.cena_zakupu_brutto ? parseFloat(product.cena_zakupu_brutto).toFixed(2) : '-'} <span style={{ fontSize: '7px' }}>brutto</span></span>
          </span>
        )}
      </div>

      {/* Marża */}
      <div style={{ ...cellBase, justifyContent: 'center' }}>
        {product.margin_percent != null ? (
          <span style={{
            fontWeight: '600', fontSize: '9px',
            color: parseFloat(product.margin_percent) >= 30 ? 'var(--success)' :
                   parseFloat(product.margin_percent) >= 15 ? 'var(--warning)' : 'var(--danger)'
          }}>
            {parseFloat(product.margin_percent).toFixed(0)}%
          </span>
        ) : (
          <span style={{ color: 'var(--text-muted)', fontSize: '9px' }}>-</span>
        )}
      </div>

      {/* VAT */}
      <div style={{ ...cellBase, justifyContent: 'center' }}>
        {isEditing ? (
          <select style={editInput({ width: '50px', padding: '0.1rem', textAlign: 'center' })}
            value={tempValues.stawka_vat !== undefined ? tempValues.stawka_vat : editingProduct.stawka_vat}
            onChange={(e) => { updateTempValue('stawka_vat', e.target.value); commitTempValue('stawka_vat', e.target.value); }}
          >
            {vatRates.length > 0 ? vatRates.map(rate => (
              <option key={rate.id} value={rate.rate}>
                {rate.rate === -2 ? 'NP' : rate.rate === -1 ? 'ZW' : `${rate.rate}%`}
              </option>
            )) : (
              <>
                <option value="23">23%</option><option value="8">8%</option>
                <option value="5">5%</option><option value="0">0%</option>
                <option value="-1">ZW</option><option value="-2">NP</option>
              </>
            )}
          </select>
        ) : (
          <span style={{
            padding: '1px 3px', borderRadius: '2px', fontSize: '8px', fontWeight: '600',
            backgroundColor: product.stawka_vat === -2 ? 'var(--text-muted)' : product.stawka_vat === -1 ? 'var(--warning)' : 'var(--info)',
            color: product.stawka_vat === -1 ? 'var(--text)' : 'white'
          }}>
            {product.stawka_vat === -2 ? 'NP' : product.stawka_vat === -1 ? 'ZW' : `${product.stawka_vat || '23'}%`}
          </span>
        )}
      </div>

      {/* Akcje */}
      <div style={{ ...cellBase, justifyContent: 'center', gap: '2px', overflow: 'visible' }}>
        {isEditing ? (
          <>
            <button type="button" style={actionBtn('var(--success)')} onClick={onSaveProduct} title="Zapisz">✓</button>
            <button type="button" style={actionBtn('var(--text-muted)')} onClick={onCancelEdit} title="Anuluj">✕</button>
          </>
        ) : (
          <ActionMenu actions={[
              { icon: '📊', label: 'Historia', color: 'var(--success)', onClick: () => onShowHistory(product) },
              canEditProducts && { icon: '✏️', label: 'Edytuj', color: 'var(--info)', onClick: () => onEditProduct(product) },
              canDeleteProducts && { icon: '🗑️', label: 'Usuń', color: 'var(--danger)', onClick: () => onDeleteProduct(product.id) },
              canExcludeFromDiscounts && {
                icon: product.wykluczony_z_rabatow ? '🚫' : '💰',
                label: product.wykluczony_z_rabatow ? 'Wykluczony z rabatów' : 'Wykluczenie z rabatów',
                color: product.wykluczony_z_rabatow ? 'var(--danger)' : 'var(--text-muted)',
                onClick: () => onToggleDiscountExclusion(product)
              },
            ]} />
        )}
      </div>
    </div>
  );
};

export default React.memo(VirtualizedProductRow);
